import { ProductItemStatus } from "../enums/productItems";
import { SellingMode } from "../enums/sellingMode";
import { Product, ProductItem } from "../types/products";

/** Partial sales are tracked by quantity for measured / unit-based when enabled on the product. */
export const supportsPartialSoldTracking = (
  sellingMode: SellingMode,
  isPartialSellingAllowed = false
): boolean =>
  (sellingMode === SellingMode.MEASURED ||
    sellingMode === SellingMode.UNIT_BASED) &&
  isPartialSellingAllowed;

/** Units/liters sold from a stock line that is still in inventory. */
export const partialSoldQuantity = (item: ProductItem): number => {
  const total = item.totalQuantity ?? 0;
  const remaining = item.remainingQuantity ?? 0;
  return Math.max(0, total - remaining);
};

export const itemHasReservedQuantity = (item: ProductItem): boolean =>
  (item.reservedQuantity ?? 0) > 0 || item.state === ProductItemStatus.RESERVED;

export const itemHasSoldQuantity = (
  item: ProductItem,
  product: Pick<Product, "sellingMode" | "isPartialSellingAllowed">
): boolean => {
  if (item.state === ProductItemStatus.SOLD) {
    return true;
  }

  if (
    supportsPartialSoldTracking(
      product.sellingMode,
      product.isPartialSellingAllowed
    ) &&
    (item.state === ProductItemStatus.IN_STOCK ||
      item.state === ProductItemStatus.RESERVED)
  ) {
    return partialSoldQuantity(item) > 0;
  }

  return false;
};

/** Stat-card / dropdown filters that match on quantities, not only lifecycle state. */
export const usesQuantityBasedStatusFilter = (
  statusFilter: string,
  product: Pick<Product, "sellingMode" | "isPartialSellingAllowed">
): boolean => {
  if (!statusFilter) {
    return false;
  }

  if (statusFilter === ProductItemStatus.RESERVED) {
    return true;
  }

  if (statusFilter === ProductItemStatus.SOLD) {
    return supportsPartialSoldTracking(
      product.sellingMode,
      product.isPartialSellingAllowed
    );
  }

  return false;
};

export const itemMatchesInventoryStatusFilter = (
  item: ProductItem,
  statusFilter: string,
  product: Pick<Product, "sellingMode" | "isPartialSellingAllowed">
): boolean => {
  if (!statusFilter) {
    return true;
  }

  if (statusFilter === ProductItemStatus.RESERVED) {
    return itemHasReservedQuantity(item);
  }

  if (statusFilter === ProductItemStatus.SOLD) {
    return itemHasSoldQuantity(item, product);
  }

  return item.state === statusFilter;
};
